import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import Layout from "@/components/Layout";
import Home from "@/pages/Home";
import About from "@/pages/About";
import Services from "@/pages/Services";
import Training from "@/pages/Training";
import AISolutions from "@/pages/AISolutions";
import WhatYouWillBuild from "@/pages/WhatYouWillBuild";
import Contact from "@/pages/Contact";
import Quote from "@/pages/Quote";
import { Privacy, Terms, Accessibility } from "@/pages/Legal";
import Insights from "@/pages/Insights";
import FirstClickToFirstChat from "@/pages/FirstClickToFirstChat";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminTestimonials from "@/pages/AdminTestimonials";
import DeckEditor from "@/pages/DeckEditor";
import PublicDeck from "@/pages/PublicDeck";
import ProtectedAdminRoute from "@/components/ProtectedAdminRoute";
import ChatWidget from "@/components/ChatWidget";
import { AuthProvider } from "@/context/AuthContext";

const ScrollToTop = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash && !hash.includes("session_id=")) {
      const el = document.getElementById(hash.slice(1));
      if (el) { el.scrollIntoView({ behavior: "smooth", block: "start" }); return; }
    }
    window.scrollTo({ top: 0, behavior: "instant" in window ? "instant" : "auto" });
  }, [pathname, hash]);
  return null;
};

// Detects OAuth session_id in the URL fragment and routes to /auth/callback synchronously.
const AppRoutes = () => {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      {/* Public marketing site (wrapped in Layout) */}
      <Route element={<LayoutWrapper />}>
        <Route path="/" element={<Home />} />
        <Route path="/about" element={<About />} />
        <Route path="/services" element={<Services />} />
        <Route path="/training" element={<Training />} />
        <Route path="/ai-solutions" element={<AISolutions />} />
        <Route path="/what-you-will-build" element={<WhatYouWillBuild />} />
        <Route path="/insights" element={<Insights />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/quote" element={<Quote />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/accessibility" element={<Accessibility />} />
        <Route path="/first-click-to-first-chat" element={<FirstClickToFirstChat />} />
      </Route>

      {/* Auth */}
      <Route path="/login" element={<Login />} />
      <Route path="/auth/callback" element={<AuthCallback />} />

      {/* Admin */}
      <Route element={<ProtectedAdminRoute />}>
        <Route path="/admin" element={<AdminDashboard />} />
        <Route path="/admin/decks/:id" element={<DeckEditor />} />
        <Route path="/admin/testimonials" element={<AdminTestimonials />} />
      </Route>

      {/* Public deck viewer */}
      <Route path="/d/:slug" element={<PublicDeck />} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

const LayoutWrapper = () => {
  // eslint-disable-next-line
  const RRD = require("react-router-dom");
  const { Outlet } = RRD;
  return <Layout><Outlet /></Layout>;
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <ScrollToTop />
          <AppRoutes />
          <ChatWidget />
          <Toaster richColors position="top-right" />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
